import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Box,
  Tooltip,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { Elements } from "@stripe/react-stripe-js";
import React, { useContext, useMemo } from "react";

import { EquidefiMarkdown } from "@equidefi/portals/components/Markdown";
import { MAXIMUM_STRIPE_AMOUNT } from "@equidefi/shared/constants/payments";
import { Text } from "@equidefi/ui";

import StripePaymentForm from "../../../components/StripePaymentForm";
import { WorkflowButton } from "../../../components/workflow/WorkflowButton";
import { CurrencyFormatter } from "../../../constants/format";
import { APPEARANCE } from "../../../constants/stripe";
import { PaymentStepContext } from "./context";

const AccordionSection = React.forwardRef(
  (
    { title, description, children, alertText, panelProps = {}, ...rest },
    ref,
  ) => {
    return (
      <AccordionItem {...rest} ref={ref} my="2">
        {({ isExpanded }) => {
          return (
            <Box
              borderRadius="lg"
              p={2}
              border={isExpanded ? "1px solid" : null}>
              <AccordionButton>
                <input
                  type="radio"
                  style={{ marginRight: 5, width: "16px", height: "16px" }}
                  checked={isExpanded}
                />
                <VStack align="start" spacing={0} flex={1} ml={2}>
                  <Text fontWeight="bold" m="0">
                    {title}
                  </Text>
                  {description && (
                    <Text textStyle="body2" color="gray.600" m="0">
                      {description}
                    </Text>
                  )}
                </VStack>
                <AccordionIcon ml="auto" />
              </AccordionButton>
              <AccordionPanel {...panelProps}>
                {alertText && (
                  <Alert
                    status="warning"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="equidefi.yellow"
                    boxShadow="sm"
                    mb={4}>
                    <AlertIcon />
                    <Text textStyle="body2" m={0}>
                      {alertText}
                    </Text>
                  </Alert>
                )}
                {children}
              </AccordionPanel>
            </Box>
          );
        }}
      </AccordionItem>
    );
  },
);

export const PaymentAccordion = ({
  onSubmitManualPayment = () => {},
  isManualPaymentLoading = false,
  supportsStripePayment,
  onSectionChange,
  paymentSettings,
  stripe,
  onReady = () => {},
  investment,
  offering,
  redirectUrl = `/offerings/${offering?.slug}/${investment?.id}/payment/processing`,
}) => {
  const { clientSecret, paymentIntentId } = useContext(PaymentStepContext);

  const options = useMemo(() => {
    return {
      clientSecret,
      options: {
        theme: "stripe",
        appearance: APPEARANCE,
      },
    };
  }, [clientSecret]);

  return (
    <Accordion variant="card" allowToggle onChange={onSectionChange}>
      <Tooltip
        label={
          !supportsStripePayment
            ? `Your payment is over our current limit of ${CurrencyFormatter.format(
                MAXIMUM_STRIPE_AMOUNT,
              )} for Credit Card or ACH.`
            : null
        }
        hasArrow
        openDelay={250}>
        <AccordionSection
          title="Credit Card, Digital Wallet or ACH"
          description="The company pays the transaction fee."
          isDisabled={!supportsStripePayment}
          panelProps={{ minHeight: "200px" }}>
          <Text textStyle="body2" m="0">
            <Alert
              status="info"
              borderRadius="lg"
              border="1px solid"
              borderColor="equidefi.blue"
              boxShadow="sm"
              mb={4}>
              <VStack align="flex-start">
                <HStack>
                  <AlertIcon alignSelf="flex-start" mr="1" />
                  <Text textStyle="body2" fontWeight="bold" m="0">
                    Credit Card Payments
                  </Text>
                </HStack>
                <Text textStyle="body2" m="0">
                  When paying by credit card, a temporary payment authorization
                  will be placed on your card. Your card will only be charged
                  once your investment is accepted.
                </Text>
              </VStack>
            </Alert>

            {clientSecret && (
              <Elements stripe={stripe} options={options}>
                <StripePaymentForm
                  investmentId={investment?.id}
                  paymentIntentId={paymentIntentId}
                  returnPath={redirectUrl}
                  offering={offering}
                />
              </Elements>
            )}
          </Text>
        </AccordionSection>
      </Tooltip>

      <AccordionSection
        title="Wire Transfer"
        description="Manual SWIFT or Bank Wire Payments"
        alertText="It may take 1 business day to complete the transaction.">
        <Text textStyle="body2">
          Please initiate your wire to the account listed below:
        </Text>

        {paymentSettings.wireInstructions?.length && (
          <EquidefiMarkdown textStyle="body2" m={0}>
            {paymentSettings.wireInstructions}
          </EquidefiMarkdown>
        )}

        <WorkflowButton
          isLoading={isManualPaymentLoading}
          onClick={() => onSubmitManualPayment()}>
          Continue
        </WorkflowButton>
      </AccordionSection>

      <AccordionSection
        title="Check"
        description="Send a check by mail."
        alertText="It may take several days to complete the transaction.">
        <Text textStyle="body2">
          Include the investment amount and send your check to:
        </Text>

        {paymentSettings.checkInstructions?.length && (
          <EquidefiMarkdown textStyle="body2" m={0}>
            {paymentSettings.checkInstructions}
          </EquidefiMarkdown>
        )}

        <WorkflowButton
          isLoading={isManualPaymentLoading}
          onClick={() => onSubmitManualPayment()}>
          Continue
        </WorkflowButton>
      </AccordionSection>
    </Accordion>
  );
};
